import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, SelectChangeEvent, Switch, TextField } from '@mui/material';
import { useMutation } from '@apollo/client';
import { useToast } from './ui/use-toast';
import { Button } from './ui/button';
import Dialog from './ui/dialog';
import { config } from '../config';
import { SalesPageStates } from '../constants/sales-pages';
import { IRowData } from './SalesPagesTable';
import CHANGE_SALES_PAGE_STATE from '../pages/sales-pages/queries/changeSalesPageState';
import CHANGE_USER_STATE from '../pages/sales-pages/queries/changeUserState';
import CHANGE_ALL_USER_SALES_PAGES_STATE from '../pages/sales-pages/queries/changeAllUserSalesPagesState';
import SAVE_ROW_CHANGES from '../pages/sales-pages/queries/saveRowChanges';
import GET_SALES_PAGES from '../pages/sales-pages/queries/getSalesPages';

interface SalesPagesModalProps {
    salesPage: IRowData;
    open: boolean;
    setOpen: (open: boolean) => void;
}

function SalesPagesModal({ salesPage, open, setOpen }: SalesPagesModalProps) {
    const { t } = useTranslation();
    const { toast } = useToast();

    const [row, setRow] = useState<IRowData>(salesPage);

    useEffect(() => {
        if (salesPage) {
            setRow(salesPage);
        }
    }, [salesPage]);

    const servicePrice =
        row && `${(row.servicePrice / 100).toFixed(2).replace('.', ',')}€`;

    const [userStateMutationVariables, setUserStateMutationVariables] =
        useState<{
            state: number;
            userId: number;
        }>();

    const [setUserState] = useMutation(CHANGE_USER_STATE, {
        variables: userStateMutationVariables,
        onError: () =>
            toast({
                title: t('error.change-user-state'),
                variant: 'destructive',
            }),
    });

    const [setAllUserSalesPagesState] = useMutation(
        CHANGE_ALL_USER_SALES_PAGES_STATE,
        {
            variables: userStateMutationVariables,
            onError: () =>
                toast({
                    title: t('error.change-all-sales-pages-state'),
                    variant: 'destructive',
                }),
            refetchQueries: () => [
                {
                    query: GET_SALES_PAGES,
                    variables: {
                        searchTerm: '%%',
                    },
                },
            ],
        },
    );

    const changeUserState = async (isActive: boolean, userId: number) => {
        const state = isActive ? 1 : 0;
        await setUserStateMutationVariables({ state, userId });
        await setUserState();
        setAllUserSalesPagesState();
    };

    const [setSalesPageState] = useMutation(CHANGE_SALES_PAGE_STATE, {
        onError: () =>
            toast({
                title: t('error.change-sales-page-state'),
                variant: 'destructive',
            }),
        refetchQueries: () => [
            {
                query: GET_SALES_PAGES,
                variables: {
                    searchTerm: '%%',
                },
            },
        ],
    });

    const [saveRowFieldsToDb, { loading: saveRowFieldsToDbInProgress }] =
        useMutation(SAVE_ROW_CHANGES, {
            onCompleted: (result) => {
                const affectedRows = result?.update_sales_pages?.affected_rows;
                if (affectedRows === 1) {
                    toast({
                        title: t('ui-feedback.save-success'),
                    });
                }
            },
            onError: () =>
                toast({
                    title: t('error.save-row-changes'),
                    variant: 'destructive',
                }),
            refetchQueries: () => [
                {
                    query: GET_SALES_PAGES,
                    variables: {
                        searchTerm: '%%',
                    },
                },
            ],
        });

    const handleSalesPageStateChange = (event: SelectChangeEvent<number>) => {
        const newState = event.target.value as number;
        setRow({
            ...row,
            state: newState,
        });
        setSalesPageState({
            variables: {
                state: newState,
                publicPath: row.publicPath,
            },
        });
    };

    const handleSaveChanges = () => {
        saveRowFieldsToDb({
            variables: {
                publicPath: row.publicPath,
                serviceName: row.serviceName,
                serviceDescription: row.serviceDescription,
                serviceVat: row.serviceVat,
            },
        });
    };

    return (
        <Dialog
            title={t('sales-page.edit-sales-page')}
            open={open}
            setOpen={setOpen}
        >
            <div className="grid sm:grid-cols-2 gap-4">
                <div className="mb-4">
                    <div className="font-bold text-lg">{t('user.user')}:</div>
                    <div>
                        <b>{t('user.full-name')}: </b>
                        {row?.salesPagesUsers?.firstName}{' '}
                        {row?.salesPagesUsers?.lastName}
                    </div>
                    <div>
                        <b>{t('user.email')}:</b> {row?.salesPagesUsers?.email}
                    </div>
                    <div>
                        <b>{t('user.phone')}:</b> {row?.salesPagesUsers?.phone}
                    </div>
                    <div>
                        <b>{t('user.language')}: </b>
                        {t(`language.${row?.salesPagesUsers?.language}`)}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                        <b>{t('sales-page.active')}</b>
                        <Switch
                            checked={!!row?.salesPagesUsers?.state}
                            onChange={(val) => {
                                setRow({
                                    ...row,
                                    salesPagesUsers: {
                                        ...row?.salesPagesUsers,
                                        state: val.target.checked ? 1 : 0,
                                    },
                                });
                                changeUserState(
                                    val.target.checked,
                                    row?.salesPagesUsers?.id,
                                );
                            }}
                        />
                    </div>
                </div>
                <div className="mb-4">
                    <div className="font-bold text-lg">
                        {t('sales-page.sales-page')}:
                    </div>
                    <div>
                        <b>{t('sales-page.service-price')}: </b>
                        {servicePrice}
                    </div>
                    <div>
                        <b>{t('sales-page.creation-date')}:</b>{' '}
                        {row?.creationDate}
                    </div>
                    <div>
                        <b>{t('sales-page.occupation')}:</b>{' '}
                        {t(`occupations:${row?.occupationId}`)}
                    </div>
                    <div>
                        <b>{t('sales-page.sales-page-link')}: </b>
                        <Link
                            href={`${config.EEZYPAY_WEB_HOST}/pay/${row?.publicPath}`}
                            target="_blank"
                        >
                            {row?.publicPath}
                        </Link>
                    </div>
                    {row?.aavaId && (
                        <div>
                            <b>Aava ID: </b>
                            <Link
                                href={`${config.AAVA_HOST}/aava/#/sales_pages/${row?.aavaId}`}
                                target="_blank"
                            >
                                {row?.aavaId}
                            </Link>
                        </div>
                    )}
                </div>
                <div className="sm:col-span-2 space-y-4">
                    <TextField
                        multiline
                        value={row?.serviceName}
                        onChange={(e) => {
                            setRow({
                                ...row,
                                serviceName: e.target.value,
                            });
                        }}
                        id="service-name"
                        label={t('sales-page.service-name')}
                        sx={{ width: '100%' }}
                        size="small"
                    />
                    <TextField
                        multiline
                        value={row?.serviceDescription || ''}
                        onChange={(e) => {
                            setRow({
                                ...row,
                                serviceDescription: e.target.value,
                            });
                        }}
                        id="service-description"
                        label={t('sales-page.service-description')}
                        sx={{ width: '100%' }}
                        size="small"
                    />
                    <div className="flex gap-4 items-end flex-wrap">
                        <TextField
                            value={row?.serviceVat}
                            onChange={(e) => {
                                setRow({
                                    ...row,
                                    serviceVat:
                                        Number(e.target.value) ||
                                        (Number(e.target.value) === 0
                                            ? 0
                                            : row.serviceVat),
                                });
                            }}
                            id="service-vat"
                            label={t('sales-page.service-vat')}
                            size="small"
                            sx={{ width: 120 }}
                        />
                        <TextField
                            select
                            size="small"
                            sx={{ minWidth: 200 }}
                            label={t('sales-page.state')}
                            value={row?.state}
                            onChange={(e) =>
                                handleSalesPageStateChange(
                                    e as SelectChangeEvent<number>,
                                )
                            }
                            SelectProps={{ native: true }}
                        >
                            <option value={SalesPageStates.Closed}>
                                {t('sales-page.closed')}
                            </option>
                            <option value={SalesPageStates.Active}>
                                {t('sales-page.active')}
                            </option>
                            <option value={SalesPageStates.WaitingForApproval}>
                                {t('sales-page.waiting-for-approval')}
                            </option>
                        </TextField>
                        <Button
                            onClick={handleSaveChanges}
                            disabled={saveRowFieldsToDbInProgress}
                        >
                            {t('button.save-changes')}
                        </Button>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

export default SalesPagesModal;
